import React, { Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import LoadingSpinner from "./components/LoadingSpinner";
import { Login } from "./pages/Login";
import { MustResetPassword } from "./pages/MustResetPassword";
import Layout from "./components/Layout";

// Lazy-loaded page components for code splitting
const Profile = React.lazy(() => import("./pages/Profile"));
const UIKitTestPage = React.lazy(() => import("./pages/UIKitTestPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

// Inventory Planning views
const InventoryStatusPage = React.lazy(() => import("./pages/InventoryStatusPage"));
const InventoryProductAlerts = React.lazy(() => import("./pages/InventoryProductAlerts"));
const MetricsOverviewPage = React.lazy(() => import("./pages/MetricsOverviewPage"));
const FbaReplenishmentPage = React.lazy(() => import("./pages/FbaReplenishmentPage"));
const KittingPage = React.lazy(() => import("./pages/KittingPage"));
const SalesForecastPage = React.lazy(() => import("./pages/SalesForecastPage"));
// Resolution Flow
const ProductResolvePage = React.lazy(() => import("./pages/ProductResolvePage"));
const CreatePurchaseOrderPage = React.lazy(() => import("./pages/CreatePurchaseOrderPage"));
const CreateRebalancePage = React.lazy(() => import("./pages/CreateRebalancePage"));

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: "always",
    },
  },
});

/** Component for Suspense fallback during lazy loading */
const LazyLoadingFallback = (): React.JSX.Element => <LoadingSpinner size="large" />;

/**
 * Renders lazy-loaded route element with Suspense boundary
 * Ensures proper type safety for React.lazy components
 */
const withLazySuspense = (component: React.LazyExoticComponent<React.FC>): React.JSX.Element => (
  <Suspense fallback={<LazyLoadingFallback />}>{React.createElement(component)}</Suspense>
);

/**
 * Main App component - Inventory Planning Frontend Boilerplate
 * Provides routing, authentication, and query client context
 *
 * This boilerplate includes:
 * - Authentication (login, logout, profile)
 * - Status
 * - UI Kit reference page
 * - Dark/light theme support
 */
function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />

              {/* Password Reset Route (Authenticated but not in Layout) */}
              <Route path="/must-reset-password" element={<MustResetPassword />} />

              {/* Protected Routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Routes>
                        <Route path="/" element={<Navigate to="/inventory/metrics" />} />

                        {/* Inventory Planning Routes */}
                        <Route path="/inventory" element={<Navigate to="/inventory/metrics" />} />
                        <Route
                          path="/inventory/status"
                          element={withLazySuspense(InventoryStatusPage)}
                        />
                        <Route
                          path="/inventory/alerts"
                          element={withLazySuspense(InventoryProductAlerts)}
                        />
                        <Route
                          path="/inventory/metrics"
                          element={withLazySuspense(MetricsOverviewPage)}
                        />
                        <Route
                          path="/inventory/fba-replenishment"
                          element={withLazySuspense(FbaReplenishmentPage)}
                        />
                        {/* Resolution Flow Routes */}
                        <Route
                          path="/inventory/resolve/:productId"
                          element={withLazySuspense(ProductResolvePage)}
                        />
                        <Route
                          path="/inventory/create-po/:productId"
                          element={withLazySuspense(CreatePurchaseOrderPage)}
                        />
                        <Route
                          path="/inventory/create-rebalance/:productId"
                          element={withLazySuspense(CreateRebalancePage)}
                        />

                        <Route path="/inventory/kitting" element={withLazySuspense(KittingPage)} />
                        <Route
                          path="/inventory/forecasts"
                          element={withLazySuspense(SalesForecastPage)}
                        />
                        <Route path="/ui-kit" element={withLazySuspense(UIKitTestPage)} />
                        <Route path="/profile" element={withLazySuspense(Profile)} />

                        {/* 404 Catch-all Route - Must be last */}
                        <Route path="*" element={withLazySuspense(NotFound)} />
                      </Routes>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
